import { useEffect, useReducer } from 'react';

import { localNow } from '@/utils/local-now';

import {
  type ClockDirection,
  type ClockState,
  isRunning,
  readClock,
} from './model';

const shown = (value: number, direction: ClockDirection): number =>
  direction === 'down' ? Math.ceil(value) : Math.floor(value);

const msUntilNextBoundary = (
  value: number,
  direction: ClockDirection,
): number => {
  const fraction = value - Math.floor(value);
  const ms = direction === 'down' ? fraction * 1000 : (1 - fraction) * 1000;

  return Math.max(1, Math.ceil(ms));
};

export const useClock = (
  clock: ClockState,
  direction: ClockDirection,
): number => {
  const [, redraw] = useReducer((n: number) => n + 1, 0);
  const value = readClock(clock, direction, localNow());

  // No dependency array: the effect re-arms after every render, and every
  // render happens either because the clock changed or because the previous
  // timeout fired.
  useEffect(() => {
    if (!isRunning(clock) || (direction === 'down' && value <= 0)) {
      return;
    }

    const timeout = setTimeout(redraw, msUntilNextBoundary(value, direction));

    return () => clearTimeout(timeout);
  });

  return shown(value, direction);
};
